import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  DATA_QUALITY_ENTITY_LABELS,
  DATA_QUALITY_FIELD_LABELS,
  dataQualityApi,
  type DataQualityEntity,
  type NormalizationPreview,
} from '../api/dataQuality'
import { jobsApi, type MyJobState } from '../api/jobs'
import { Page, PageHeader, Tabs, Callout, LoadingState, Card, DataTable, Checkbox, Button, useConfirm } from '../components/ui'

/**
 * F-093 (TASK-0347, REQ-236-001 / REQ-237-001): qualita' dei dati anagrafici. L'amministratore vede per
 * ogni campo quante righe cambierebbero (con gli esempi prima -> dopo), sceglie i campi e conferma: la
 * bonifica gira in background. Nulla parte da solo e l'anteprima non scrive mai. Pagina solo Admin (il
 * server risponde 403 agli altri).
 */
const ENTITIES = Object.keys(DATA_QUALITY_ENTITY_LABELS) as DataQualityEntity[]

const label = (field: string) => DATA_QUALITY_FIELD_LABELS[field] ?? field

export function DataQualityPage() {
  const askConfirm = useConfirm()
  const [entity, setEntity] = useState<DataQualityEntity>('contacts')
  const [preview, setPreview] = useState<NormalizationPreview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [open, setOpen] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [applying, setApplying] = useState(false)
  const [jobId, setJobId] = useState<number | null>(null)
  const [job, setJob] = useState<MyJobState | null>(null)
  const timer = useRef<number | null>(null)

  const load = useCallback(async (which: DataQualityEntity) => {
    setLoading(true)
    setError(null)
    setConfirmed(false)
    try {
      const p = await dataQualityApi.preview(which)
      setPreview(p)
      setSelected(new Set(p.fields.filter(f => f.changes > 0).map(f => f.field)))
    } catch (e) {
      setPreview(null)
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load(entity) }, [entity, load])

  // Segue il job finche' non e' concluso.
  useEffect(() => {
    if (jobId === null) return
    let stopped = false
    const tick = async () => {
      try {
        const state = await jobsApi.mine(jobId)
        if (stopped) return
        setJob(state)
        if (!state.finished) timer.current = window.setTimeout(() => { void tick() }, 2000)
      } catch (e) {
        if (!stopped) setError(e instanceof Error ? e.message : 'Errore nel controllo del job')
      }
    }
    void tick()
    return () => {
      stopped = true
      if (timer.current !== null) window.clearTimeout(timer.current)
    }
  }, [jobId])

  function toggle(field: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
  }

  const selectedChanges = preview?.fields.filter(f => selected.has(f.field)).reduce((s, f) => s + f.changes, 0) ?? 0

  async function apply() {
    if (!preview || selected.size === 0 || !confirmed) return
    const names = [...selected].map(label).join(', ')
    if (!(await askConfirm({ title: 'Correggi i dati', message: `Correggere ${DATA_QUALITY_ENTITY_LABELS[entity].toLowerCase()}: ${names}? ` +
      `Circa ${selectedChanges} valori verranno riscritti. L'operazione parte in background.`, confirmLabel: 'Correggi' }))) return
    setApplying(true)
    setError(null)
    try {
      const res = await dataQualityApi.apply(entity, [...selected])
      setJob(null)
      setJobId(res.jobId)
      setConfirmed(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nell\'avvio della bonifica')
    } finally {
      setApplying(false)
    }
  }

  const result = job?.result as
    | { changedRows?: number; changedValues?: number; skipped?: number; scanned?: number }
    | null
    | undefined

  return (
    <Page>
      <PageHeader
        title="Qualità dei dati"
        icon="cleaning_services"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
      />

      <Callout tone="info">
        Telefoni senza separatori e senza il &quot;.0&quot; lasciato da Excel, email in minuscolo, codici fiscali
        in maiuscolo, partite IVA senza &quot;IT&quot;, CAP a cinque cifre, province a due lettere, spazi doppi
        eliminati. L&apos;anteprima non modifica nulla; nessun valore viene mai scartato.
      </Callout>

      <Tabs
        items={ENTITIES.map(e => ({ id: e, label: DATA_QUALITY_ENTITY_LABELS[e] }))}
        value={entity}
        onChange={id => setEntity(id as DataQualityEntity)}
        aria-label="Tipo di anagrafica"
      />

      {error && <Callout tone="error">{error}</Callout>}

      {jobId !== null && (
        <Callout tone="info">
          <strong>Bonifica in corso o conclusa</strong> (job #{jobId}).{' '}
          {job === null && 'Controllo lo stato…'}
          {job && !job.finished && (
            <>
              Righe controllate: {job.done ?? 0} su {job.total ?? '—'}.
            </>
          )}
          {job?.finished && job.status === 'processed' && result && (
            <>
              Righe corrette: <strong>{result.changedRows ?? 0}</strong> (valori: {result.changedValues ?? 0}), righe
              saltate: {result.skipped ?? 0}.
            </>
          )}
          {job?.finished && job.status !== 'processed' && (
            <>Esito: {job.status}{job.error ? ` — ${job.error}` : ''}.</>
          )}{' '}
          <Link to="/jobs">Console dei job</Link>
        </Callout>
      )}

      {loading ? (
        <LoadingState />
      ) : preview ? (
        <>
          <p className="mb-0">
            Righe esaminate: <strong>{preview.scanned}</strong> · righe da correggere:{' '}
            <strong>{preview.changedRows}</strong>
          </p>

          <Card variant="outlined" padding="none">
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Correggi</th>
                  <th>Campo</th>
                  <th>Righe che cambiano</th>
                  <th>Esempi</th>
                </tr>
              </thead>
              <tbody>
                {preview.fields.map(f => (
                  <tr key={f.field}>
                    <td>
                      <Checkbox
                        label={<span className="md-sr-only">{`Correggi ${label(f.field)}`}</span>}
                        checked={selected.has(f.field)}
                        disabled={f.changes === 0}
                        onChange={() => toggle(f.field)}
                      />
                    </td>
                    <td className="md-typescale-emphasized-body-medium">{label(f.field)}</td>
                    <td className="md-num">
                      {f.changes === 0 ? <span className="text-muted">già a posto</span> : f.changes}
                    </td>
                    <td>
                      {f.examples.length === 0 ? (
                        '—'
                      ) : (
                        <>
                          <Button
                            variant="text"
                            size="sm"
                            onClick={() => setOpen(open === f.field ? null : f.field)}
                          >
                            {open === f.field ? 'Nascondi' : `Mostra ${f.examples.length} esempi`}
                          </Button>
                          {open === f.field && (
                            <ul className="list-unstyled md-typescale-body-small mb-0 mt-1">
                              {f.examples.map(x => (
                                <li key={`${x.id}-${x.field}`}>
                                  <span className="text-muted">#{x.id}</span>{' '}
                                  <code>{x.before ?? '(vuoto)'}</code> → <code>{x.after ?? '(vuoto)'}</code>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </Card>

          <Checkbox
            id="dq-confirm"
            checked={confirmed}
            onChange={e => setConfirmed(e.target.checked)}
            label="Ho visto l'anteprima e confermo la correzione dei campi selezionati."
          />
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="tonal"
              disabled={applying || !confirmed || selected.size === 0}
              onClick={() => { void apply() }}
            >
              {applying ? 'Avvio…' : `Applica la bonifica (${selectedChanges} valori)`}
            </Button>
            <Button variant="outlined" onClick={() => { void load(entity) }}>
              Aggiorna l&apos;anteprima
            </Button>
          </div>
        </>
      ) : null}
    </Page>
  )
}
